import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, MoreVertical } from 'lucide-react';
import { AppHeader, MainContent, AddButton } from '../components';
import { CreateGroupModal, GroupMenuModal } from '../components/modals';
import { useAuth, useAppModals, useDrawer, useMediaQuery } from '../hooks';
import { listContextGroups, createContextGroup, deleteContextGroup, type ContextGroup } from '../api/services';

export const GroupsScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const { isAuthenticated, email, fullName, avatarUrl } = useAuth();
  const { openAuthPrompt } = useAppModals();
  const { openDrawer } = useDrawer();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ContextGroup | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const groupsQuery = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const res = await listContextGroups();
      return res.data ?? [];
    },
  });

  const groups = groupsQuery.data ?? [];
  const isLoading = groupsQuery.isLoading;

  const handleCreate = async (name: string, icon: string, color: string) => {
    await createContextGroup({ name, icon, color });
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const handleDelete = async (group: ContextGroup) => {
    setIsDeleting(true);
    try {
      await deleteContextGroup(group.id);
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setShowMenuModal(false);
      setSelectedGroup(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAuthClick = () => {
    if (isAuthenticated) openDrawer();
    else openAuthPrompt();
  };

  return (
    <>
      <AppHeader
        isAuthenticated={isAuthenticated}
        fullName={fullName}
        email={email}
        avatarUrl={avatarUrl}
        onAuthClick={handleAuthClick}
        hideAuthOnDesktop={isDesktop}
      />
      <MainContent>
        <div style={{ padding: 'var(--space-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
            <h1 style={{ fontSize: 'var(--font-xl)', color: 'var(--color-text-primary)' }}>
              {t('groups.title', 'Groups')}
            </h1>
            <AddButton onClick={() => setShowCreateModal(true)} label={t('groups.create', 'New group')} />
          </div>

          {isLoading ? (
            <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
          ) : groups.length === 0 ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              {t('groups.emptyDescription', 'Organize your links into groups. Tap + to create one.')}
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 'var(--space-md)' }}>
              {groups.map((g) => (
                <div
                  key={g.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/groups/${g.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/groups/${g.id}`)}
                  style={{
                    padding: 'var(--space-lg)',
                    borderRadius: 'var(--radius-lg)',
                    background: 'var(--color-surface)',
                    border: `2px solid ${g.color || 'var(--color-border)'}`,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                    position: 'relative',
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedGroup(g);
                      setShowMenuModal(true);
                    }}
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    <MoreVertical size={18} />
                  </button>
                  <FolderOpen size={32} style={{ color: g.color || 'var(--color-accent)' }} />
                  <span style={{ fontSize: 'var(--font-sm)', color: 'var(--color-text-primary)', textAlign: 'center' }}>
                    {g.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </MainContent>

      <CreateGroupModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreate}
      />
      <GroupMenuModal
        isOpen={showMenuModal}
        group={selectedGroup}
        onClose={() => { setShowMenuModal(false); setSelectedGroup(null); }}
        onDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};
